/**
 * App shell — renders the routes contributed by the feature registry
 * (`src/features/registry.ts`). The shell MUST work with an empty registry: with
 * no `features/<name>/routes.tsx` modules present, only the placeholder home
 * screen renders.
 *
 * Each route owns its page frame: authenticated features render through
 * `components/layout/AppLayout` (sidebar + header + main), public pages bring
 * their own, and the shell's own fallbacks wrap themselves in `ShellFrame`.
 */

import type { ReactNode } from 'react';
import { BrowserRouter, Link, useRoutes, type RouteObject } from 'react-router-dom';

import { featureRoutes } from '@/features/registry';

/** Frame for the shell-owned fallback screens only. */
function ShellFrame({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="flex items-center border-b px-6 py-3">
        <Link to="/" className="text-sm font-semibold tracking-tight">
          Cluster Workspace
        </Link>
      </header>
      <main className="flex-1 px-6 py-8">{children}</main>
    </div>
  );
}

function HomePlaceholder() {
  return (
    <section className="mx-auto mt-16 max-w-lg rounded-lg border bg-card p-8 text-card-foreground">
      <h1 className="text-2xl font-semibold tracking-tight">Cluster Workspace</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {featureRoutes.length === 0
          ? 'No features registered yet. Add src/features/<name>/routes.tsx exporting `routes` to populate this app.'
          : 'Feature routes are registered — pick one from the navigation.'}
      </p>
    </section>
  );
}

function NotFoundPage() {
  return (
    <section className="mx-auto mt-16 max-w-lg text-center">
      <h1 className="text-3xl font-semibold tracking-tight">404</h1>
      <p className="mt-2 text-sm text-muted-foreground">This page does not exist.</p>
      <Link to="/" className="mt-4 inline-block text-sm text-primary underline">
        Back to workspace home
      </Link>
    </section>
  );
}

/**
 * Feature routes are matched **first**: the shell index route is only a
 * fallback for an empty registry, so a feature can claim `/` (the auth feature
 * mounts the protected dashboard shell there) without the shell naming it. The
 * `*` catch-all stays last. Shell-owned fallbacks keep their own frame.
 */
function AppRoutes() {
  const routes: RouteObject[] = [
    ...featureRoutes,
    {
      index: true,
      element: (
        <ShellFrame>
          <HomePlaceholder />
        </ShellFrame>
      ),
    },
    {
      path: '*',
      element: (
        <ShellFrame>
          <NotFoundPage />
        </ShellFrame>
      ),
    },
  ];
  return useRoutes(routes);
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
